import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { useCoachProfileInColumn } from '@lib/pga-coach/coach-profile-layout'
import useStyles from './coach-tools-row.styles'

const HEADING = 'Tools & Technology'
const HEADING_ID = 'coach-tools-row-heading'

/**
 * Returns the deduped, trimmed list of tool names a coach has populated.
 * Mirrors the filtering used in `coach-bio` so SSR + tests stay aligned.
 */
function normalizeTools(tools) {
  if (!Array.isArray(tools)) return []
  const seen = new Set()
  const out = []
  tools.forEach((raw) => {
    const t = typeof raw === 'string' ? raw.trim() : ''
    if (t && !seen.has(t)) {
      seen.add(t)
      out.push(t)
    }
  })
  return out
}

export function coachToolsRowHasContent(coach) {
  return normalizeTools(coach?.toolJsonData).length > 0
}

/**
 * Simple Tools & Technology text list for the flagged About bio. Sits
 * beside the Accreditations bullets after the membership banner —
 * bold `Typography` subtitle1 / h3 header, body1 list underneath, not
 * compact pills. Hash destination `#coach-tools-row-heading` stays on
 * the heading.
 *
 * Auto-hidden when the list is empty / only blanks.
 */
function CoachToolsRow({
  coach = null,
  embedded = false,
}) {
  const tools = useMemo(() => normalizeTools(coach?.toolJsonData), [coach?.toolJsonData])
  const inColumn = useCoachProfileInColumn()
  const compact = embedded || inColumn
  const styles = useStyles({ inColumn: compact, embedded: compact })

  if (tools.length === 0) return null

  const rootClassName = compact ? styles.rootEmbedded : styles.root
  const innerClassName = compact
    ? `${styles.inner} ${styles.innerEmbedded}`
    : styles.inner

  return (
    <Box
      component="section"
      className={rootClassName}
      aria-labelledby={HEADING_ID}
    >
      <Box className={innerClassName}>
        <Typography
          id={HEADING_ID}
          variant="subtitle1"
          component="h3"
          className={styles.heading}
        >
          {HEADING}
        </Typography>
        <Box component="ul" className={styles.list}>
          {tools.map((tool) => (
            <Typography
              key={tool}
              component="li"
              variant="body1"
              className={styles.item}
            >
              {tool}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

CoachToolsRow.propTypes = {
  coach: PropTypes.shape({
    toolJsonData: PropTypes.arrayOf(PropTypes.string),
  }),
  /** When true, renders inside the About bio (no full-bleed band chrome). */
  embedded: PropTypes.bool,
}

export default CoachToolsRow
